import React, { useMemo, useState } from 'react';
import { GripVertical, Trash2, Image as ImageIcon, Plus, ArrowRight, Search, Check, X, CheckCircle2, XCircle, SearchX, Copy, TrendingUp, TrendingDown, BarChart3, Clock, Layers, PlusCircle, Package, Ban, PackageX, Edit3, Leaf, Wheat, Flame, AlertTriangle } from 'lucide-react';
import { StationIconBadge } from './BranchStationsSection';
const DIETARY_META: Record<string, {
  label: string;
  icon: any;
  classes: string;
}> = {
  vegetarian: {
    label: 'Veg',
    icon: Leaf,
    classes: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  },
  vegan: {
    label: 'Vegan',
    icon: Leaf,
    classes: 'bg-green-50 text-green-700 border-green-200'
  },
  'gluten-free': {
    label: 'GF',
    icon: Wheat,
    classes: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  spicy: {
    label: 'Spicy',
    icon: Flame,
    classes: 'bg-red-50 text-red-700 border-red-200'
  }
};
import { motion, AnimatePresence } from 'framer-motion';
import { Toggle } from './Toggle';
import { ItemAnalyticsModal, ItemAnalytics } from './ItemAnalyticsModal';
import { ScheduleModal, Schedule, DEFAULT_SCHEDULE, formatSchedule, isAvailableNow } from './ScheduleModal';
import { AddMenuItemModal, NewMenuItem, Variation, AvailableMethods, ALL_METHODS_AVAILABLE, OptionGroup } from './AddMenuItemModal';
import { useSettings } from './SettingsContext';
import { useTakeawayPackages } from './TakeawayPackagesContext';
import { useModifiers } from './ModifiersContext';
import { useCategories } from './CategoriesContext';
import { useBranches } from './BranchesContext';
export type MenuItem = {
  id: number;
  name: string;
  description: string;
  price: number;
  active: boolean;
  imageUrl?: string;
  ordersThisWeek: number;
  trendPct: number;
  popularity: number;
  revenueThisWeek: number;
  weeklyData: {
    day: string;
    orders: number;
  }[];
  hourlyData: {
    hour: string;
    orders: number;
  }[];
  rating: number;
  reviewCount: number;
  schedule: Schedule;
  variations: Variation[];
  modifierIds: string[];
  packageId?: string;
  outOfStock?: boolean;
  availableMethods?: AvailableMethods;
  optionGroups?: OptionGroup[];
  stationId?: string;
  dietaryTags?: string[];
  allergens?: string[];
  stockCount?: number;
  lowStockThreshold?: number;
};
const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const hours = ['7am', '9am', '11am', '1pm', '3pm', '5pm', '7pm', '9pm'];
function makeAnalytics(seed: number, baseOrders: number) {
  const rand = (offset: number) => {
    const x = Math.sin(seed * 9.7 + offset * 2.3) * 10000;
    return x - Math.floor(x);
  };
  const weeklyData = days.map((day, i) => ({
    day,
    orders: Math.round(baseOrders * (0.6 + rand(i) * 0.8))
  }));
  const hourlyData = hours.map((hour, i) => ({
    hour,
    orders: Math.round(baseOrders / 4 * (0.3 + rand(i + 20) * 1.6))
  }));
  return {
    weeklyData,
    hourlyData
  };
}
const BREAKFAST_SCHEDULE: Schedule = {
  alwaysAvailable: false,
  days: [0, 1, 2, 3, 4, 5, 6],
  startTime: '06:00',
  endTime: '11:00'
};
const LUNCH_SCHEDULE: Schedule = {
  alwaysAvailable: false,
  days: [0, 1, 2, 3, 4, 5, 6],
  startTime: '11:00',
  endTime: '15:00'
};
const initialItems: MenuItem[] = [{
  id: 1,
  name: 'Breakfast Test',
  description: '',
  price: 1,
  active: true,
  ordersThisWeek: 42,
  trendPct: -8.3,
  popularity: 28,
  revenueThisWeek: 42,
  rating: 3.8,
  reviewCount: 12,
  schedule: BREAKFAST_SCHEDULE,
  packageId: 'pkg_standard',
  variations: [],
  modifierIds: [],
  ...makeAnalytics(1, 6)
}, {
  id: 2,
  name: 'American Breakfast',
  description: 'Two eggs any style, served with beef sausage, home fries, a mini pancake, and toast bread.',
  price: 15,
  active: true,
  ordersThisWeek: 312,
  trendPct: 12.5,
  popularity: 87,
  revenueThisWeek: 4680,
  rating: 4.7,
  reviewCount: 184,
  schedule: BREAKFAST_SCHEDULE,
  variations: [{
    id: 'v1',
    name: 'Single portion',
    price: 15
  }, {
    id: 'v2',
    name: 'Hungry',
    price: 19
  }, {
    id: 'v3',
    name: 'Sharing platter',
    price: 28
  }],
  modifierIds: ['mod_eggs', 'mod_bacon', 'mod_avocado', 'mod_hotsauce'],
  optionGroups: [{
    id: 'og_eggs',
    name: 'How would you like your eggs?',
    required: true,
    minSelections: 1,
    maxSelections: 1,
    options: [{
      id: 'opt_e1',
      name: 'Sunny side up',
      priceDelta: 0
    }, {
      id: 'opt_e2',
      name: 'Over easy',
      priceDelta: 0
    }, {
      id: 'opt_e3',
      name: 'Scrambled',
      priceDelta: 0
    }, {
      id: 'opt_e4',
      name: 'Poached',
      priceDelta: 1
    }]
  }, {
    id: 'og_toast',
    name: 'Toast choice',
    required: true,
    minSelections: 1,
    maxSelections: 1,
    options: [{
      id: 'opt_t1',
      name: 'White',
      priceDelta: 0
    }, {
      id: 'opt_t2',
      name: 'Wheat',
      priceDelta: 0
    }, {
      id: 'opt_t3',
      name: 'Sourdough',
      priceDelta: 1.5
    }, {
      id: 'opt_t4',
      name: 'Gluten-free',
      priceDelta: 2
    }]
  }],
  ...makeAnalytics(2, 45)
}, {
  id: 3,
  name: 'Salmon Wrap',
  description: 'A warm tortilla filled with grilled salmon, sliced boiled egg, fresh vegetables, and melted cheese—a hearty, energizing start to your morning.',
  price: 18,
  active: true,
  ordersThisWeek: 198,
  trendPct: 24.1,
  popularity: 73,
  revenueThisWeek: 3564,
  rating: 4.5,
  reviewCount: 96,
  schedule: {
    alwaysAvailable: false,
    days: [0, 1, 2, 3, 4, 5, 6],
    startTime: '07:00',
    endTime: '15:00'
  },
  packageId: 'pkg_wrap',
  variations: [],
  modifierIds: ['mod_salmon', 'mod_salad'],
  ...makeAnalytics(3, 28),
  outOfStock: true
}, {
  id: 4,
  name: 'Avocado & Salmon Toast',
  description: 'Toasted bread topped with creamy avocado and grilled salmon, served with a green salad and roasted potatoes.',
  price: 16,
  active: true,
  ordersThisWeek: 156,
  trendPct: 5.2,
  popularity: 64,
  revenueThisWeek: 2496,
  rating: 4.6,
  reviewCount: 71,
  schedule: DEFAULT_SCHEDULE,
  packageId: 'pkg_standard',
  variations: [],
  modifierIds: [],
  ...makeAnalytics(4, 22)
}, {
  id: 5,
  name: 'Avocado Toast',
  description: '',
  price: 11,
  active: true,
  ordersThisWeek: 89,
  trendPct: -3.4,
  popularity: 41,
  revenueThisWeek: 979,
  rating: 4.2,
  reviewCount: 34,
  schedule: BREAKFAST_SCHEDULE,
  variations: [{
    id: 'v1',
    name: 'Half',
    price: 7
  }, {
    id: 'v2',
    name: 'Full',
    price: 11
  }],
  modifierIds: ['mod_poached', 'mod_feta'],
  ...makeAnalytics(5, 13)
}];
type FilterStatus = 'all' | 'active' | 'inactive' | 'sold-out';
// Tiny inline SVG sparkline
function Sparkline({
  data,
  trend
}: {
  data: {
    orders: number;
  }[];
  trend: number;
}) {
  const values = data.map(d => d.orders);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const w = 60;
  const h = 22;
  const stepX = w / (values.length - 1);
  const points = values.map((v, i) => `${i * stepX},${h - (v - min) / range * h}`).join(' ');
  const color = trend >= 0 ? '#10b981' : '#ef4444';
  return <svg width={w} height={h} className="overflow-visible" data-id="element-2456">
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" data-id="element-2457" />
    </svg>;
}
export function MenuItemsPanel({
  categoryName
}: {
  categoryName: string;
}) {
  const {
    getPackage
  } = useTakeawayPackages();
  const {
    settings
  } = useSettings();
  const {
    packages
  } = useTakeawayPackages();
  const {
    modifiers
  } = useModifiers();
  const {
    categories
  } = useCategories();
  const {
    branches
  } = useBranches();
  // Pool canonical stations across branches so any item can resolve its station
  // by id regardless of which branch we're viewing.
  const stationMap = useMemo(() => {
    const m: Record<string, {
      id: string;
      name: string;
      color: string;
      icon: string;
    }> = {};
    branches.forEach(b => {
      b.stations.forEach(s => {
        if (!m[s.id]) m[s.id] = s;
      });
    });
    return m;
  }, [branches]);
  const currentCategory = useMemo(() => categories.find(c => c.name === categoryName), [categories, categoryName]);
  const resolveStationId = (item: MenuItem): string | undefined => item.stationId || currentCategory?.defaultStationId;
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [analyticsItem, setAnalyticsItem] = useState<ItemAnalytics | null>(null);
  const [recentlyDuplicated, setRecentlyDuplicated] = useState<Set<number>>(new Set());
  const [scheduleEditTarget, setScheduleEditTarget] = useState<{
    mode: 'single';
    itemId: number;
  } | {
    mode: 'bulk';
  } | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [items, setItems] = useState<MenuItem[]>(initialItems);
  const [editTargetId, setEditTargetId] = useState<number | null>(null);
  const editTarget = editTargetId !== null ? items.find(i => i.id === editTargetId) ?? null : null;
  const editInitial: NewMenuItem | undefined = editTarget ? {
    name: editTarget.name,
    description: editTarget.description,
    price: editTarget.price,
    active: editTarget.active,
    imageUrl: editTarget.imageUrl,
    schedule: editTarget.schedule,
    variations: editTarget.variations,
    modifierIds: editTarget.modifierIds,
    packageId: editTarget.packageId,
    availableMethods: editTarget.availableMethods ?? ALL_METHODS_AVAILABLE,
    optionGroups: editTarget.optionGroups ?? [],
    stationId: editTarget.stationId,
    dietaryTags: editTarget.dietaryTags ?? [],
    allergens: editTarget.allergens ?? [],
    stockCount: editTarget.stockCount,
    lowStockThreshold: editTarget.lowStockThreshold
  } : undefined;
  const saveEditedItem = (data: NewMenuItem) => {
    if (editTargetId === null) return;
    setItems(prev => prev.map(it => it.id === editTargetId ? {
      ...it,
      name: data.name,
      description: data.description,
      price: Number(data.price) || 0,
      active: data.active,
      imageUrl: data.imageUrl,
      schedule: data.schedule,
      variations: data.variations,
      modifierIds: data.modifierIds,
      packageId: data.packageId,
      availableMethods: data.availableMethods,
      optionGroups: data.optionGroups,
      stationId: data.stationId,
      dietaryTags: data.dietaryTags,
      allergens: data.allergens,
      stockCount: data.trackInventory ? Number(data.stockCount) || 0 : undefined,
      lowStockThreshold: data.trackInventory ? Number(data.lowStockThreshold) || undefined : undefined
    } : it));
    setEditTargetId(null);
  };
  const toggleItem = (id: number) => {
    setItems(items.map(item => item.id === id ? {
      ...item,
      active: !item.active
    } : item));
  };
  const toggleOutOfStock = (id: number) => {
    setItems(items.map(item => item.id === id ? {
      ...item,
      outOfStock: !item.outOfStock
    } : item));
  };
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const duplicateItem = (id: number) => {
    const idx = items.findIndex(i => i.id === id);
    if (idx === -1) return;
    const source = items[idx];
    const newId = Math.max(...items.map(i => i.id)) + 1;
    const copy: MenuItem = {
      ...source,
      id: newId,
      name: `${source.name} (Copy)`,
      ordersThisWeek: 0,
      revenueThisWeek: 0,
      popularity: 0,
      trendPct: 0,
      rating: 0,
      reviewCount: 0,
      weeklyData: days.map(day => ({
        day,
        orders: 0
      })),
      hourlyData: hours.map(hour => ({
        hour,
        orders: 0
      })),
      variations: source.variations.map(v => ({
        ...v
      })),
      modifierIds: [...source.modifierIds]
    };
    const next = [...items];
    next.splice(idx + 1, 0, copy);
    setItems(next);
    setRecentlyDuplicated(prev => new Set(prev).add(newId));
    setTimeout(() => {
      setRecentlyDuplicated(prev => {
        const n = new Set(prev);
        n.delete(newId);
        return n;
      });
    }, 1500);
  };
  const createItem = (data: NewMenuItem) => {
    const nextId = items.length === 0 ? 1 : Math.max(...items.map(i => i.id)) + 1;
    const newItem: MenuItem = {
      id: nextId,
      name: data.name,
      description: data.description,
      price: Number(data.price) || 0,
      active: data.active,
      imageUrl: data.imageUrl,
      schedule: data.schedule,
      variations: data.variations,
      modifierIds: data.modifierIds,
      ordersThisWeek: 0,
      trendPct: 0,
      popularity: 0,
      revenueThisWeek: 0,
      rating: 0,
      reviewCount: 0,
      weeklyData: days.map(day => ({
        day,
        orders: 0
      })),
      hourlyData: hours.map(hour => ({
        hour,
        orders: 0
      })),
      packageId: data.packageId,
      availableMethods: data.availableMethods,
      optionGroups: data.optionGroups,
      stationId: data.stationId,
      dietaryTags: data.dietaryTags,
      allergens: data.allergens,
      stockCount: data.trackInventory ? Number(data.stockCount) || 0 : undefined,
      lowStockThreshold: data.trackInventory ? Number(data.lowStockThreshold) || undefined : undefined
    };
    setItems([newItem, ...items]);
    setRecentlyDuplicated(prev => new Set(prev).add(nextId));
    setTimeout(() => {
      setRecentlyDuplicated(prev => {
        const n = new Set(prev);
        n.delete(nextId);
        return n;
      });
    }, 1500);
  };
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = !searchQuery.trim() || item.name.toLowerCase().includes(searchQuery.toLowerCase()) || item.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filter === 'all' || filter === 'active' && item.active || filter === 'inactive' && !item.active || filter === 'sold-out' && item.outOfStock;
      return matchesSearch && matchesFilter;
    });
  }, [items, searchQuery, filter]);
  const activeCount = items.filter(i => i.active).length;
  const inactiveCount = items.length - activeCount;
  const soldOutCount = items.filter(i => i.outOfStock).length;
  const selectedCount = selectedIds.size;
  const allFilteredSelected = filteredItems.length > 0 && filteredItems.every(item => selectedIds.has(item.id));
  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredItems.forEach(item => next.delete(item.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredItems.forEach(item => next.add(item.id));
        return next;
      });
    }
  };
  const bulkActivate = () => {
    setItems(items.map(item => selectedIds.has(item.id) ? {
      ...item,
      active: true
    } : item));
    clearSelection();
  };
  const bulkDeactivate = () => {
    setItems(items.map(item => selectedIds.has(item.id) ? {
      ...item,
      active: false
    } : item));
    clearSelection();
  };
  const bulkDelete = () => {
    setItems(items.filter(item => !selectedIds.has(item.id)));
    clearSelection();
  };
  const bulkDuplicate = () => {
    const toDuplicate = items.filter(i => selectedIds.has(i.id));
    let nextId = Math.max(...items.map(i => i.id)) + 1;
    const newIds: number[] = [];
    const copies: MenuItem[] = toDuplicate.map(source => {
      const id = nextId++;
      newIds.push(id);
      return {
        ...source,
        id,
        name: `${source.name} (Copy)`,
        ordersThisWeek: 0,
        revenueThisWeek: 0,
        popularity: 0,
        trendPct: 0,
        rating: 0,
        reviewCount: 0,
        weeklyData: days.map(day => ({
          day,
          orders: 0
        })),
        hourlyData: hours.map(hour => ({
          hour,
          orders: 0
        })),
        variations: source.variations.map(v => ({
          ...v
        })),
        modifierIds: [...source.modifierIds]
      };
    });
    setItems([...items, ...copies]);
    setRecentlyDuplicated(new Set(newIds));
    setTimeout(() => setRecentlyDuplicated(new Set()), 1500);
    clearSelection();
  };
  const saveSchedule = (schedule: Schedule) => {
    if (!scheduleEditTarget) return;
    if (scheduleEditTarget.mode === 'single') {
      const id = scheduleEditTarget.itemId;
      setItems(items.map(i => i.id === id ? {
        ...i,
        schedule
      } : i));
    } else {
      setItems(items.map(i => selectedIds.has(i.id) ? {
        ...i,
        schedule
      } : i));
      clearSelection();
    }
    setScheduleEditTarget(null);
  };
  const scheduleInitial: Schedule = (() => {
    if (!scheduleEditTarget) return DEFAULT_SCHEDULE;
    if (scheduleEditTarget.mode === 'single') {
      return items.find(i => i.id === scheduleEditTarget.itemId)?.schedule ?? DEFAULT_SCHEDULE;
    }
    // Bulk: if all selected share the same schedule signature, use it; else default
    const selected = items.filter(i => selectedIds.has(i.id));
    if (selected.length === 0) return DEFAULT_SCHEDULE;
    const first = selected[0].schedule;
    const sig = JSON.stringify(first);
    const allSame = selected.every(i => JSON.stringify(i.schedule) === sig);
    return allSame ? first : DEFAULT_SCHEDULE;
  })();
  const scheduleTargetName: string | undefined = scheduleEditTarget?.mode === 'single' ? items.find(i => i.id === scheduleEditTarget.itemId)?.name : undefined;
  // Aggregate all unique stations across all branches for the modal
  const allStations = useMemo(() => {
    const map = new Map();
    branches.forEach(b => {
      b.stations.forEach(s => {
        if (!map.has(s.id)) map.set(s.id, s);
      });
    });
    return Array.from(map.values());
  }, [branches]);
  return <div className="flex flex-col gap-4" data-id="element-2458">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1" data-id="element-2459">
        <h2 className="text-lg font-bold text-gray-900" data-id="element-2460">
          Menu Items - {categoryName}
        </h2>
        <button onClick={() => setAddModalOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-sm w-full sm:w-auto" data-id="element-2461">
          <Plus size={16} data-id="element-2462" />
          Add Menu Item
        </button>
      </div>

      {/* Search + Filter Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex flex-col sm:flex-row gap-3 sm:items-center" data-id="element-2463">
        <div className="relative flex-1" data-id="element-2464">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-2465" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search menu items..." className="w-full pl-9 pr-9 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-2466" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5" data-id="element-2467">
              <X size={14} data-id="element-2468" />
            </button>}
        </div>

        <div className="flex gap-1.5 bg-gray-100 rounded-lg p-1" data-id="element-2469">
          {[{
          value: 'all' as FilterStatus,
          label: 'All',
          count: items.length
        }, {
          value: 'active' as FilterStatus,
          label: 'Active',
          count: activeCount
        }, {
          value: 'inactive' as FilterStatus,
          label: 'Inactive',
          count: inactiveCount
        }, {
          value: 'sold-out' as FilterStatus,
          label: 'Sold out',
          count: soldOutCount
        }].map(tab => <button key={tab.value} onClick={() => setFilter(tab.value)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors flex items-center gap-1.5 ${filter === tab.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2470">
              {tab.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${filter === tab.value ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-600'}`} data-id="element-2471">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      <AnimatePresence data-id="element-2472">
        {selectedCount > 0 && <motion.div initial={{
        opacity: 0,
        y: -8,
        height: 0
      }} animate={{
        opacity: 1,
        y: 0,
        height: 'auto'
      }} exit={{
        opacity: 0,
        y: -8,
        height: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="sticky top-0 z-20 overflow-hidden" data-id="element-2473">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl shadow-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-id="element-2474">
              <div className="flex items-center gap-3" data-id="element-2475">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0" data-id="element-2476">
                  <Check size={16} strokeWidth={3} data-id="element-2477" />
                </div>
                <span className="text-sm font-semibold" data-id="element-2478">
                  {selectedCount} {selectedCount === 1 ? 'item' : 'items'}{' '}
                  selected
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap" data-id="element-2479">
                <button onClick={() => setScheduleEditTarget({
              mode: 'bulk'
            })} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors" data-id="element-2480">
                  <Clock size={14} data-id="element-2481" />
                  Set schedule
                </button>
                <button onClick={bulkDuplicate} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors" data-id="element-2482">
                  <Copy size={14} data-id="element-2483" />
                  Duplicate
                </button>
                <button onClick={bulkActivate} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors" data-id="element-2484">
                  <CheckCircle2 size={14} data-id="element-2485" />
                  Activate
                </button>
                <button onClick={bulkDeactivate} className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors" data-id="element-2486">
                  <XCircle size={14} data-id="element-2487" />
                  Deactivate
                </button>
                <button onClick={bulkDelete} className="flex items-center gap-1.5 bg-red-600/90 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors" data-id="element-2488">
                  <Trash2 size={14} data-id="element-2489" />
                  Delete
                </button>
                <button onClick={clearSelection} className="text-white/80 hover:text-white p-1.5" aria-label="Clear selection" data-id="element-2490">
                  <X size={16} data-id="element-2491" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Select All bar */}
      {filteredItems.length > 0 && <div className="flex items-center justify-between px-1 -mb-1" data-id="element-2492">
          <button onClick={toggleSelectAllFiltered} className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors group" data-id="element-2493">
            <span className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${allFilteredSelected ? 'bg-orange-500 border-orange-500' : 'border-gray-300 group-hover:border-gray-400 bg-white'}`} data-id="element-2494">
              {allFilteredSelected && <Check size={11} className="text-white" strokeWidth={3} data-id="element-2495" />}
            </span>
            {allFilteredSelected ? 'Deselect all' : 'Select all'}
          </button>
          <span className="text-xs text-gray-400" data-id="element-2496">
            Showing {filteredItems.length} of {items.length}
          </span>
        </div>}

      {/* Items List */}
      <div className="flex flex-col gap-4" data-id="element-2497">
        <AnimatePresence mode="popLayout" data-id="element-2498">
          {filteredItems.map(item => {
          const isSelected = selectedIds.has(item.id);
          const isFresh = recentlyDuplicated.has(item.id);
          const trendUp = item.trendPct >= 0;
          const availableNow = isAvailableNow(item.schedule);
          return <motion.div key={item.id} layout initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0,
            boxShadow: isFresh ? '0 0 0 3px rgba(249, 115, 22, 0.4)' : '0 1px 2px 0 rgba(0,0,0,0.05)'
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.25
          }} className={`bg-white rounded-xl border p-4 md:p-5 flex flex-col sm:flex-row gap-4 transition-colors ${isSelected ? 'border-orange-300 ring-2 ring-orange-100' : 'border-gray-200'}`} data-id="element-2499">
                {/* Checkbox + Drag + Image */}
                <div className="flex gap-3 sm:w-auto sm:flex-shrink-0 items-start" data-id="element-2500">
                  <button onClick={() => toggleSelect(item.id)} className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-orange-500 border-orange-500' : 'border-gray-300 hover:border-orange-400 bg-white'}`} aria-label={isSelected ? 'Deselect item' : 'Select item'} data-id="element-2501">
                    {isSelected && <Check size={13} className="text-white" strokeWidth={3} data-id="element-2502" />}
                  </button>
                  <div className="pt-1 cursor-grab hidden sm:block" data-id="element-2503">
                    <GripVertical size={18} className="text-gray-300" data-id="element-2504" />
                  </div>
                  <div className="w-[64px] h-[64px] sm:w-[72px] sm:h-[72px] bg-gray-200/80 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-2505">
                    {item.imageUrl ? <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" data-id="element-2506" /> : <ImageIcon size={24} className="text-gray-400" data-id="element-2507" />}
                  </div>
                  <div className="flex-1 sm:hidden flex flex-col justify-center min-w-0" data-id="element-2508">
                    <h3 className="font-bold text-gray-900 text-[15px] truncate" data-id="element-2509">
                      {item.name}
                    </h3>
                    <div className="text-sm text-gray-700 mt-1 font-medium" data-id="element-2510">
                      ETB {item.price}
                    </div>
                  </div>
                </div>

                {/* Middle: Description + Analytics */}
                <div className="flex-1 flex flex-col justify-between py-0.5 min-w-0" data-id="element-2511">
                  <div data-id="element-2512">
                    <h3 className="font-bold text-gray-900 text-[15px] hidden sm:block" data-id="element-2513">
                      {item.name}
                    </h3>
                    {item.description && <p className="text-sm text-gray-500 sm:mt-1.5 leading-relaxed pr-0 sm:pr-4 line-clamp-2" data-id="element-2514">
                        {item.description}
                      </p>}
                    <div className="text-sm text-gray-700 mt-3 font-medium hidden sm:block" data-id="element-2515">
                      ETB {item.price}
                    </div>

                    {/* Schedule badge */}
                    <button onClick={() => setScheduleEditTarget({
                  mode: 'single',
                  itemId: item.id
                })} className={`mt-3 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors group ${item.schedule.alwaysAvailable ? 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100' : availableNow ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300' : 'border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-300'}`} data-id="element-2516">
                      <span className="relative flex h-2 w-2 flex-shrink-0" data-id="element-2517">
                        {availableNow && !item.schedule.alwaysAvailable && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" data-id="element-2518" />}
                        <span className={`relative inline-flex h-2 w-2 rounded-full ${item.schedule.alwaysAvailable ? 'bg-gray-400' : availableNow ? 'bg-emerald-500' : 'bg-amber-500'}`} data-id="element-2519" />
                      </span>
                      <Clock size={12} className="opacity-70" data-id="element-2520" />
                      <span data-id="element-2521">{formatSchedule(item.schedule)}</span>
                      {!item.schedule.alwaysAvailable && !availableNow && <span className="text-[10px] uppercase tracking-wider font-bold opacity-70" data-id="element-2522">
                          Off-hours
                        </span>}
                    </button>

                    {/* Variations + Modifiers + Package indicators */}
                    {(() => {
                  const hasVariations = item.variations.length > 0;
                  const hasModifiers = item.modifierIds.length > 0;
                  const hasPackage = item.packageId && getPackage(item.packageId);
                  return <div className="flex items-center gap-2 mt-2 flex-wrap" data-id="element-2523">
                          {hasVariations && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-50 text-orange-600 text-[11px] font-semibold border border-orange-100" data-id="element-2524">
                              <Layers size={11} data-id="element-2525" />
                              {item.variations.length}{' '}
                              {item.variations.length === 1 ? 'variation' : 'variations'}
                            </span>}
                          {hasModifiers && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-[11px] font-semibold border border-purple-100" data-id="element-2526">
                              <PlusCircle size={11} data-id="element-2527" />
                              {item.modifierIds.length}{' '}
                              {item.modifierIds.length === 1 ? 'add-on' : 'add-ons'}
                            </span>}
                          {hasPackage && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-semibold border border-emerald-100" data-id="element-2528">
                              <Package size={11} data-id="element-2529" />
                              {getPackage(item.packageId)?.name} · +ETB{' '}
                              {getPackage(item.packageId)?.price.toFixed(2)}
                            </span>}
                          <AvailableMethodsChip methods={item.availableMethods} data-id="element-2530" />
                          <OptionGroupsChip groups={item.optionGroups} data-id="element-2531" />
                          {/* Station routing chip */}
                          {(() => {
                      const sid = resolveStationId(item);
                      const station = sid ? stationMap[sid] : undefined;
                      if (!station) return null;
                      const isOverride = !!item.stationId;
                      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-50 text-gray-700 text-[11px] font-semibold border border-gray-200" title={isOverride ? `Routes to ${station.name} (item override)` : `Routes to ${station.name} (category default)`} data-id="element-2532">
                                <StationIconBadge icon={station.icon} color={station.color} size={10} data-id="element-2533" />
                                {station.name}
                                {isOverride && <span className="text-[9px] uppercase tracking-wide text-orange-600 font-bold" data-id="element-2534">
                                    ·custom
                                  </span>}
                              </span>;
                    })()}
                          {/* Dietary tags */}
                          {(item.dietaryTags || []).map(tag => {
                      const meta = DIETARY_META[tag];
                      if (!meta) return null;
                      const I = meta.icon;
                      return <span key={tag} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${meta.classes}`} data-id="element-2535">
                                <I size={11} data-id="element-2536" />
                                {meta.label}
                              </span>;
                    })}
                          {/* Stock indicator (only when tracked) */}
                          {typeof item.stockCount === 'number' && (() => {
                      const count = item.stockCount;
                      const threshold = item.lowStockThreshold ?? 5;
                      const isOut = count <= 0;
                      const isLow = !isOut && count <= threshold;
                      if (isOut) {
                        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-50 text-red-700 text-[11px] font-semibold border border-red-200" data-id="element-2537">
                                    <AlertTriangle size={11} data-id="element-2538" />
                                    Out of stock
                                  </span>;
                      }
                      if (isLow) {
                        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[11px] font-semibold border border-amber-200" data-id="element-2539">
                                    <AlertTriangle size={11} data-id="element-2540" />
                                    Low · {count} left
                                  </span>;
                      }
                      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-semibold border border-emerald-200" data-id="element-2541">
                                  <Package size={11} data-id="element-2542" />
                                  In stock · {count}
                                </span>;
                    })()}
                        </div>;
                })()}

                    {/* Analytics row */}
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mt-3" data-id="element-2543">
                      <div className="flex items-center gap-1.5" data-id="element-2544">
                        <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide" data-id="element-2545">
                          Orders/wk
                        </span>
                        <span className="text-sm font-bold text-gray-900" data-id="element-2546">
                          {item.ordersThisWeek}
                        </span>
                      </div>
                      <div className={`flex items-center gap-1 text-xs font-semibold ${trendUp ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-2547">
                        {trendUp ? <TrendingUp size={12} data-id="element-2548" /> : <TrendingDown size={12} data-id="element-2549" />}
                        {trendUp ? '+' : ''}
                        {item.trendPct.toFixed(1)}%
                      </div>
                      {item.ordersThisWeek > 0 && <Sparkline data={item.weeklyData} trend={item.trendPct} data-id="element-2550" />}
                      <button onClick={() => setAnalyticsItem({
                    id: item.id,
                    name: item.name,
                    ordersThisWeek: item.ordersThisWeek,
                    ordersLastWeek: Math.round(item.ordersThisWeek / (1 + item.trendPct / 100)),
                    trendPct: item.trendPct,
                    popularity: item.popularity,
                    revenueThisWeek: item.revenueThisWeek,
                    weeklyData: item.weeklyData,
                    hourlyData: item.hourlyData,
                    rating: item.rating,
                    reviewCount: item.reviewCount
                  })} className="flex items-center gap-1 text-xs font-semibold text-orange-500 hover:text-orange-600 transition-colors" data-id="element-2551">
                        <BarChart3 size={12} data-id="element-2552" />
                        View analytics
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 hidden sm:flex items-center gap-2" data-id="element-2553">
                    {item.outOfStock && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wide uppercase bg-red-100 text-red-700" data-id="element-2554">
                        <Ban size={11} strokeWidth={2.5} data-id="element-2555" />
                        Sold out
                      </span>}
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wide uppercase ${item.active ? 'bg-[#dcfce7] text-[#166534]' : 'bg-gray-100 text-gray-500'}`} data-id="element-2556">
                      {item.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between sm:flex-col sm:items-end sm:justify-between sm:ml-4 py-0.5 pt-3 sm:pt-0 border-t border-gray-100 sm:border-t-0 mt-2 sm:mt-0" data-id="element-2557">
                  <div className="flex items-center gap-2 sm:hidden" data-id="element-2558">
                    {item.outOfStock && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wide uppercase bg-red-100 text-red-700" data-id="element-2559">
                        <Ban size={11} strokeWidth={2.5} data-id="element-2560" />
                        Sold out
                      </span>}
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wide uppercase ${item.active ? 'bg-[#dcfce7] text-[#166534]' : 'bg-gray-100 text-gray-500'}`} data-id="element-2561">
                      {item.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-2562">
                    <Toggle checked={item.active} onChange={() => toggleItem(item.id)} data-id="element-2563" />
                    <button onClick={() => setScheduleEditTarget({
                  mode: 'single',
                  itemId: item.id
                })} className="text-gray-400 hover:text-orange-500 transition-colors p-1" aria-label="Edit schedule" title="Schedule" data-id="element-2564">
                      <Clock size={17} data-id="element-2565" />
                    </button>
                    <button onClick={() => duplicateItem(item.id)} className="text-gray-400 hover:text-orange-500 transition-colors p-1" aria-label="Duplicate item" title="Duplicate" data-id="element-2566">
                      <Copy size={17} data-id="element-2567" />
                    </button>
                    <button onClick={() => toggleOutOfStock(item.id)} className={item.outOfStock ? 'text-red-500 hover:text-red-600 transition-colors p-1' : 'text-gray-400 hover:text-red-500 transition-colors p-1'} aria-label={item.outOfStock ? 'Mark in stock' : 'Mark out of stock'} title={item.outOfStock ? 'Mark in stock' : 'Mark out of stock'} data-id="element-2568">
                      <PackageX size={17} data-id="element-2569" />
                    </button>
                    <button className="text-red-500 hover:text-red-600 transition-colors p-1" aria-label="Delete item" title="Delete" data-id="element-2570">
                      <Trash2 size={18} data-id="element-2571" />
                    </button>
                  </div>
                  <button onClick={() => setEditTargetId(item.id)} className="text-orange-500 hover:text-orange-600 text-sm font-medium flex items-center gap-1.5 mt-0 sm:mt-4 hover:bg-orange-50 px-2 py-1 -mx-2 rounded-md transition-colors" data-id="element-2572">
                    <Edit3 size={15} data-id="element-2573" />
                    Edit
                  </button>
                </div>
              </motion.div>;
        })}
        </AnimatePresence>

        {/* Empty State */}
        {filteredItems.length === 0 && <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-xl border border-dashed border-gray-300 p-10 flex flex-col items-center justify-center text-center" data-id="element-2574">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-2575">
              <SearchX size={22} className="text-gray-400" data-id="element-2576" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm mb-1" data-id="element-2577">
              No items found
            </h3>
            <p className="text-xs text-gray-500 max-w-xs" data-id="element-2578">
              {searchQuery ? `No menu items match "${searchQuery}". Try a different search term.` : `No ${filter === 'all' ? '' : filter} items in this category.`}
            </p>
            {(searchQuery || filter !== 'all') && <button onClick={() => {
          setSearchQuery('');
          setFilter('all');
        }} className="mt-4 text-xs font-medium text-orange-500 hover:text-orange-600" data-id="element-2579">
                Clear filters
              </button>}
          </motion.div>}
      </div>
      <ItemAnalyticsModal item={analyticsItem} onClose={() => setAnalyticsItem(null)} data-id="element-2580" />
      <ScheduleModal open={scheduleEditTarget !== null} initial={scheduleInitial} itemName={scheduleTargetName} bulkCount={scheduleEditTarget?.mode === 'bulk' ? selectedIds.size : undefined} onClose={() => setScheduleEditTarget(null)} onSave={saveSchedule} data-id="element-2581" />
      {addModalOpen && <AddMenuItemModal onClose={() => setAddModalOpen(false)} onSave={createItem} packages={packages} modifiers={modifiers} categories={categories} stations={allStations} data-id="element-2582" />}
      {editTarget && <AddMenuItemModal initialData={editTarget} onClose={() => setEditTargetId(null)} onSave={saveEditedItem} packages={packages} modifiers={modifiers} categories={categories} stations={allStations} data-id="element-2583" />}
    </div>;
}
function AvailableMethodsChip({
  methods
}: {
  methods?: AvailableMethods;
}) {
  const effective = methods || ALL_METHODS_AVAILABLE;
  const enabledCount = Object.values(effective).filter(Boolean).length;
  // If available everywhere, don't clutter the row with a chip
  if (enabledCount === 4) return null;
  if (enabledCount === 0) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-50 text-red-700 text-[11px] font-semibold border border-red-100" data-id="element-2584">
        Hidden — no methods enabled
      </span>;
  }
  const labels: {
    key: keyof AvailableMethods;
    short: string;
  }[] = [{
    key: 'dineIn',
    short: 'Dine-in'
  }, {
    key: 'takeout',
    short: 'Takeout'
  }, {
    key: 'delivery',
    short: 'Delivery'
  }, {
    key: 'orderAhead',
    short: 'Order ahead'
  }];
  const enabledLabels = labels.filter(l => effective[l.key]).map(l => l.short).join(' · ');
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 text-[11px] font-semibold border border-amber-100" title={`Only available for: ${enabledLabels}`} data-id="element-2585">
      <span className="uppercase tracking-wider text-[9px] font-bold" data-id="element-2586">
        Only
      </span>
      {enabledLabels}
    </span>;
}
function OptionGroupsChip({
  groups
}: {
  groups?: OptionGroup[];
}) {
  if (!groups || groups.length === 0) return null;
  const requiredCount = groups.filter(g => g.required).length;
  const totalCount = groups.length;
  const summary = groups.map(g => g.name.trim() || 'Unnamed').slice(0, 3).join(' · ');
  const tooltip = groups.map(g => `${g.name.trim() || 'Unnamed'} (${g.options.length} options${g.required ? ', required' : ''})`).join(' • ');
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${requiredCount > 0 ? 'bg-rose-50 text-rose-700 border-rose-100' : 'bg-gray-50 text-gray-700 border-gray-200'}`} title={tooltip} data-id="element-2587">
      <span className="uppercase tracking-wider text-[9px] font-bold" data-id="element-2588">
        {requiredCount > 0 ? 'Required' : 'Options'}
      </span>
      {summary}
      {totalCount > 3 && <span className="text-gray-400" data-id="element-2589">+{totalCount - 3}</span>}
    </span>;
}